/**
 * Reading settings — the three knobs a text game is expected
 * to have, and the two it is not getting.
 *
 * **Why these three.** The story feed set the typography from the outside: 62–70 characters, a serif at
 * `1.125rem`, `1.65` leading. Those are good defaults and they are still guesses about one
 * reader — the measure is right for a laptop and long for a phone held close, the size is right
 * for most eyes and small for some. All three tokens were written as CSS custom properties on
 * `:root` precisely so this file could exist (`tokens.css` says so, verbatim), which is why the
 * whole feature is one inline style on `<html>` and no component knows it happened.
 *
 * **Why not a theme.** The brief listed one, and D6 answers it: dark only, deliberately,
 * matching the game. There is no light palette to switch to — `tokens.css` is one set of
 * surfaces — and inventing one is an art-direction decision rather than a settings-panel
 * decision (CLAUDE.md §9: game-design questions are the human's). A toggle between a theme and
 * nothing would be worse than no toggle.
 *
 * **Why not a typewriter switch.** Also on that list, and there is nothing to switch off:
 * `useFrameBuffer` publishes the *latest* narration once per animation frame and never meters
 * text out (its own docstring is emphatic about it, and the reason is that a client-side
 * animation must not own a fifty-second wait the player is already impatient with). The one
 * thing that moves is the cursor, and `prefers-reduced-motion` already stops it.
 *
 * Everything here is client-side and unauthenticated: `localStorage`, no `PATCH /me`, no BE
 * ("no BE"). A player who reads on two devices sets it twice, which is the correct
 * trade for a preference about *this screen* rather than about them.
 */
import { useEffect } from 'react';
import { create } from 'zustand';

export type NarrationSize = 'small' | 'normal' | 'large' | 'huge';
export type ReadingMeasure = 'narrow' | 'normal' | 'wide';
export type NarrationFace = 'serif' | 'sans';

export interface ReadingSettings {
  size: NarrationSize;
  measure: ReadingMeasure;
  /**
   * The narration face. `sans` exists for one specific reader rather than as a matter of
   * taste: some dyslexic readers find a serif's stroke contrast harder, and the interface
   * face is already loaded and already the fallback everything else uses.
   */
  face: NarrationFace;
}

/** The story feed's values, unchanged. The default has to *be* the design, or the design is the setting. */
export const DEFAULT_READING: ReadingSettings = {
  size: 'normal',
  measure: 'normal',
  face: 'serif',
};

/**
 * `--text-narration`. Four steps rather than a slider: a slider invites fiddling with a number
 * whose good values are four, and every step here still reads as this game rather than as a
 * text editor. `huge` is the accessibility step — 1.5rem is where the measure starts to matter
 * more than the size, which is why the two settings sit next to each other.
 */
export const SIZE_STEPS: Record<NarrationSize, string> = {
  small: '1rem',
  normal: '1.125rem',
  large: '1.3125rem',
  huge: '1.5rem',
};

/** `--measure`. 62–70 characters is `normal`; the neighbours are roughly ±25%. */
export const MEASURE_STEPS: Record<ReadingMeasure, string> = {
  narrow: '27rem',
  normal: '34rem',
  wide: '42rem',
};

export const FACE_STEPS: Record<NarrationFace, string> = {
  serif: 'var(--font-narration-serif)',
  sans: 'var(--font-ui)',
};

/**
 * The custom properties to set on `<html>`, as data.
 *
 * Pure and exported so the mapping is testable without a DOM, and — the reason it matters —
 * so that the *set* of properties is one list. A settings screen that writes three variables
 * and a reset that clears two is a reset that leaves a font size behind.
 */
export function readingVariables(settings: ReadingSettings): Record<string, string> {
  return {
    '--text-narration': SIZE_STEPS[settings.size],
    '--measure': MEASURE_STEPS[settings.measure],
    '--font-narration': FACE_STEPS[settings.face],
  };
}

const STORAGE_KEY = 'wasteland.reading';

/**
 * What is on disk, or the default — and never anything else.
 *
 * Every field is checked against its own value set rather than cast. `localStorage` outlives
 * deploys, so a value written by a version that had five size steps is a real input to a
 * version that has four, and the failure mode of trusting it is a `--text-narration:
 * undefined` that renders the whole story at the browser default.
 */
export function parseReading(raw: string | null): ReadingSettings {
  if (raw === null) return DEFAULT_READING;
  try {
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== 'object' || stored === null) return DEFAULT_READING;
    const value = stored as Partial<Record<keyof ReadingSettings, unknown>>;
    return {
      size: pick(value.size, SIZE_STEPS, DEFAULT_READING.size),
      measure: pick(value.measure, MEASURE_STEPS, DEFAULT_READING.measure),
      face: pick(value.face, FACE_STEPS, DEFAULT_READING.face),
    };
  } catch {
    return DEFAULT_READING;
  }
}

function pick<K extends string>(value: unknown, allowed: Record<K, string>, fallback: K): K {
  return typeof value === 'string' && value in allowed ? (value as K) : fallback;
}

interface ReadingStore extends ReadingSettings {
  set: <K extends keyof ReadingSettings>(key: K, value: ReadingSettings[K]) => void;
  reset: () => void;
}

/**
 * Reading is not server state and it is not turn state, so by D3's own table it is neither of
 * the two stores — but it is read by the app shell and written by a dialog three levels away,
 * which is the one thing `useState` cannot do. Zustand is already here for the turn.
 *
 * `localStorage` is touched in the setter rather than through a persistence middleware: it is
 * two lines, it fails closed in a private-mode browser that throws on write, and it keeps the
 * store a plain object in tests.
 */
export const useReadingStore = create<ReadingStore>()((set, get) => ({
  ...parseReading(readStorage()),

  set: (key, value) => {
    set({ [key]: value } as Pick<ReadingSettings, typeof key>);
    writeStorage(get());
  },

  reset: () => {
    set(DEFAULT_READING);
    writeStorage(DEFAULT_READING);
  },
}));

/**
 * Push the settings onto `<html>`, where `tokens.css` put the defaults.
 *
 * Called once, by `App`, rather than by whatever screen happens to be mounted: the story feed,
 * the epitaph and the peripheral pages all read `--measure`, and a setting that only applied
 * inside `/play` would change the page width as the player navigated.
 *
 * An inline style on the root element beats the `:root` rule in `tokens.css` by specificity
 * without touching it, so the defaults stay written where the design is argued and this only
 * ever *overrides*.
 */
export function useApplyReading(): void {
  // Three selectors rather than one returning an object: zustand compares the selected value
  // by identity, so a fresh object every render is a render loop.
  const size = useReadingStore((state) => state.size);
  const measure = useReadingStore((state) => state.measure);
  const face = useReadingStore((state) => state.face);

  useEffect(() => {
    const root = document.documentElement;
    const variables = readingVariables({ size, measure, face });
    for (const [name, value] of Object.entries(variables)) root.style.setProperty(name, value);
  }, [size, measure, face]);
}

function readStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari in private mode, and any browser with storage disabled. The defaults are a
    // perfectly good session; refusing to render because a preference could not be read is not.
    return null;
  }
}

function writeStorage(settings: ReadingSettings): void {
  const { size, measure, face } = settings;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ size, measure, face }));
  } catch {
    // Same, in the other direction: the setting still applies to this session.
  }
}
